import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useErrorInputs } from "../../../hooks/useErrorInputs";
import { useDataAuth } from "../../../hooks/useDataAuth";
import { useTranslation } from "react-i18next";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import TableContainer from "@mui/material/TableContainer";
import FormControl from "@mui/material/FormControl";
import Typography from "@mui/material/Typography";
import RadioGroup from "@mui/material/RadioGroup";
import InputLabel from "@mui/material/InputLabel";
import TextField from "@mui/material/TextField";
import FormLabel from "@mui/material/FormLabel";
import TableHead from "@mui/material/TableHead";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Radio from "@mui/material/Radio";
import Table from "@mui/material/Table";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import { fetchStorages } from "../../../services/service.service";
import { fetchServiceJson } from "../../../services/common.services";
import { validateAndEnsureArray } from "../../../utils/jsonValidator";

export const CreateFormService = forwardRef(({ dto, configuration, handleInputChange, handleDirectInputChange, handleSearchProduct, isEdit, taxesSiigo }, ref) => {
    const fieldsRequired = ['name', 'code_sku', 'unit_code', 'description'];
    // eslint-disable-next-line no-unused-vars
    const [errorInputs, _handleErrorInput, _resetErrors, validForm] = useErrorInputs({});
    const [{ userSession }] = useDataAuth();
    const { t } = useTranslation();

    const initialStateStorage = {
        id_company: '',
        id_warehouse: '',
        id_product_service: '0',
        price: '',
        cost: '',
        profit: '',
        taxes: '',
        location: '',
        stock: '',
        stock_max: '',
        stock_min: '',
        discount: ''
    }

    const [categories, setCategories] = useState([]);
    const [sublines, setSublines] = useState([]);
    const [units, setUnits] = useState([]);
    const [lines, setLines] = useState([]);
    const [brands, setBrands] = useState([]);
    const [references, setReferences] = useState([]);
    const [ansList, setAnsList] = useState([]);
    const [storage, setStorage] = useState([initialStateStorage]);

    useEffect(() => {
        if (!dto.id_company) return;
        const fetchData = async () => {
            try {
                const dataSercives = await fetchServiceJson('MENU-PRODUCT_SERVICE', userSession.token, dto.id_company);
                if (isEdit) {
                    const responseStoreages = await fetchStorages(dto.id_product_service, userSession.token);
                    if (responseStoreages.data?.length > 0) {
                        setStorage(responseStoreages.data);
                    }
                }
                if (dataSercives.data?.length > 0) {
                    dataSercives.data.forEach(item => {
                        const parsedValue = validateAndEnsureArray(item.value);
                        if (item.field === "category") setCategories(parsedValue);
                        else if (item.field === "lines") setLines(parsedValue);
                        else if (item.field === "sublines") setSublines(parsedValue);
                        else if (item.field === "ans") setAnsList(parsedValue);
                        else if (item.field === "brand") setBrands(parsedValue);
                        else if (item.field === "references") setReferences(parsedValue);
                        else if (item.field === "unitMeasuremen") setUnits(parsedValue);
                    });
                }
            } catch (e) {
                console.error("Error fetching service JSON:", e.message);
            }
        };
        fetchData();
    }, [userSession.token, dto.id_company, isEdit, dto.id_product_service]);

    useImperativeHandle(ref, () => ({
        getData: () => dto,
        errorInputs,
        validForm: () => validForm(fieldsRequired, dto)
    }));

    return (
        <>
            <Grid container spacing={1} sx={{ mt: 2 }}>
                <Grid size={{ xs: 12, md: 12 }} >
                    <FormControl fullWidth>
                        <FormLabel>{t('crm.products.type')}</FormLabel>
                        <RadioGroup row value={dto.type} onChange={({ target: { value } }) => handleDirectInputChange('type', value)}>
                            <FormControlLabel
                                value="Servicio"
                                control={<Radio />}
                                label={t('crm.products.service')}
                            />
                            <FormControlLabel
                                value="Producto"
                                control={<Radio />}
                                label={t('crm.products.product')}
                            />
                            <FormControlLabel
                                value="Cuenta"
                                control={<Radio />}
                                label={t('crm.products.account')}
                            />
                        </RadioGroup>
                    </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 12 }}>
                    <TextField fullWidth required label={t('crm.products.name')} size="small" name='name'
                        value={dto.name}
                        onChange={handleInputChange}
                        error={Boolean(errorInputs.name?.isError)}
                        helperText={errorInputs.name?.messageError || ''}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 12 }}>
                    <TextField fullWidth required label={t('crm.products.description')} size="small" name='description'
                        value={dto.description}
                        onChange={handleInputChange}
                        error={Boolean(errorInputs.description?.isError)}
                        helperText={errorInputs.description?.messageError || ''}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField fullWidth required disabled={isEdit} size="small" label={t('crm.products.code')} name='code_sku'
                        value={dto.code_sku}
                        onChange={handleInputChange}
                        onBlur={handleSearchProduct}
                        error={Boolean(errorInputs.code_sku?.isError)}
                        helperText={errorInputs.code_sku?.messageError || ''}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth required size="small"
                        error={Boolean(errorInputs.unit_code?.isError)}
                    >
                        <InputLabel>{t('crm.products.unitOfMeasurement')}</InputLabel>
                        <Select label={t('crm.products.unitOfMeasurement')} name='unit_code'
                            value={dto.unit_code}
                            // onChange={handleInputChange}
                            onChange={(e) => {
                                handleDirectInputChange('unit_code', e.target.value);
                                handleDirectInputChange('unit_description', units.find(unit => unit.code === e.target.value)?.name || '');
                            }}
                        >
                            {units.map(unit => <MenuItem key={unit.code} value={unit.code}>{unit.name}</MenuItem>)}
                        </Select>
                        {errorInputs.unit_code?.isError && <FormHelperText>{errorInputs.unit_code.messageError}</FormHelperText>}
                    </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>{t('crm.products.brand')}</InputLabel>
                        <Select label={t('crm.products.brand')} name='brand'
                            value={dto.brand}
                            onChange={handleInputChange}
                        >
                            {brands.map(brand => <MenuItem key={brand.code} value={brand.code}>{brand.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>{t('crm.products.reference')}</InputLabel>
                        <Select label={t('crm.products.reference')} name='reference'
                            value={dto.reference}
                            onChange={handleInputChange}
                        >
                            {references.map(reference => <MenuItem key={reference.code} value={reference.code}>{reference.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    {
                        configuration?.ERP === "siigo" ?
                            <FormControl fullWidth size="small">
                                <InputLabel>{t('crm.products.account_tax_iva')}</InputLabel>
                                <Select
                                    label={t('crm.products.account_tax_iva')}
                                    name='account_tax_iva'
                                    value={dto.account_tax_iva}
                                    onChange={handleInputChange}
                                >
                                    {taxesSiigo.map(cat => <MenuItem key={cat.code} value={cat.code}>{cat.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                            :
                            <TextField
                                fullWidth
                                size="small"
                                label={t('crm.products.account_tax_iva')}
                                name='account_tax_iva'
                                value={dto.account_tax_iva}
                                onChange={handleInputChange}
                            // error={Boolean(errorInputs.account_tax_iva?.isError)}
                            // helperText={errorInputs.account_tax_iva?.messageError || ''}
                            />
                    }
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        fullWidth
                        size="small"
                        label={t('crm.products.account_discount')}
                        name='account_discount'
                        value={dto.account_discount}
                        onChange={handleInputChange}
                        error={Boolean(errorInputs.account_discount?.isError)}
                        helperText={errorInputs.account_discount?.messageError || ''}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        fullWidth
                        size="small"
                        label={t('crm.products.account_tax_others')}
                        name='account_tax_others'
                        value={dto.account_tax_others}
                        onChange={handleInputChange}
                    />
                </Grid>

                {dto.type === "Servicio" && (
                    <>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>{t('crm.products.category')}</InputLabel>
                                <Select label={t('crm.products.category')} name='category'
                                    value={dto.category}
                                    onChange={handleInputChange}
                                >
                                    {categories.map(cat => <MenuItem key={cat.code} value={cat.code}>{cat.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>{t('crm.products.line')}</InputLabel>
                                <Select label={t('crm.products.line')} name='line'
                                    value={dto.line}
                                    onChange={handleInputChange}
                                >
                                    {lines.map(line => <MenuItem key={line.code} value={line.code}>{line.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>{t('crm.products.subLine')}</InputLabel>
                                <Select label={t('crm.products.subLine')} name='subline'
                                    value={dto.subline}
                                    onChange={handleInputChange}
                                >
                                    {sublines.map(subline => <MenuItem key={subline.code} value={subline.code}>{subline.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>ANS</InputLabel>
                                <Select label='ANS' name='ans'
                                    value={dto.ans}
                                    onChange={handleInputChange}
                                >
                                    {ansList.map(ans => <MenuItem key={ans.code} value={ans.code}>{ans.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                    </>
                )}
                {isEdit && (
                    <>
                        <Grid size={{ xs: 12, sm: 12 }}>
                            <Typography variant="h6">{t('productsAndServices.titleGrid')}</Typography>
                        </Grid>
                        <TableContainer component={Paper} sx={{ mt: 4 }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('productsAndServices.store')}</TableCell>
                                        <TableCell>{t('crm.products.price')}</TableCell>
                                        <TableCell>{t('crm.products.location')}</TableCell>
                                        <TableCell>{t('crm.products.stock')}</TableCell>
                                        <TableCell>{t('crm.products.discount')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {storage.map((branch, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{branch.warehouseName}</TableCell>
                                            <TableCell>{branch.price}</TableCell>
                                            <TableCell>{branch.location}</TableCell>
                                            <TableCell>{branch.stock}</TableCell>
                                            <TableCell>{branch.discount}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}

            </Grid >
        </>
    )
})